import {autobind} from 'core-decorators';
import {getDOMNodes} from 'utils';
import BaseAdapter from './base';

// https://docs.brightcove.com/en/player/brightcove-player/reference/api/vjs.Player.html

@autobind
export default class BrightcoveAdapter extends BaseAdapter {
    static DATA_ATTRS = ['data-player', 'data-account']

    static findVideos() {
        const nodes = getDOMNodes('video.video-js, video.vjs-tech');
        return nodes
            .filter(node => BrightcoveAdapter.DATA_ATTRS.every(attr => Boolean(node.getAttribute(attr))))
            .map(node => ({
                provider: 'brightcove',
                embedType: 'In-Page Embed',
                videoNode: node,
                playerId: node.getAttribute('data-player'),
                accountId: node.getAttribute('data-account'),
                videoId: node.getAttribute('data-video-id'),
            }));
    }

    getVideoDimensions() {
        const width = this.videoNode.getAttribute('width');
        const height = this.videoNode.getAttribute('height');
        if (width && height) return [parseInt(width, 10), parseInt(height, 10)];
        return super.getVideoDimensions();
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            const check = () => {
                if (window.videojs && this.videoNode.classList.contains('vjs-tech')) {
                    this.videoNode = this.videoNode.parentNode;
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();

            const timeout = () => reject('Brightcove API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        return new Promise((resolve, reject) => {
            this.enforcePlayerParameters();

            this.api = window.videojs(this.videoNode.getElementsByTagName('video')[0]);
            this.api.on('pause', () => this.setPlayerState(BaseAdapter.STATES.Paused));
            this.api.on('play', () => this.setPlayerState(BaseAdapter.STATES.Playing));
            this.api.on('seeked', () => this.setPlayerState(BaseAdapter.STATES.Seek));
            this.api.on('ended', () => this.setPlayerState(BaseAdapter.STATES.Ended));
            this.api.on('timeupdate', this.syncPlayhead);

            const check = () => {
                const duration = this.getDuration();
                if (!Number.isNaN(duration) && duration !== 0) {
                    this.handleReady();
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();

            const timeout = () => reject('Brightcove Player initialization failed');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    enforcePlayerParameters() {
        const video = this.videoNode.getElementsByTagName('video')[0];
        if (video) {
            video.setAttribute('webkit-playsinline', true);
            video.setAttribute('playsinline', true);
        }
    }

    disableCaptions() {
        const tracks = this.api.textTracks();
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = 'hidden';
        }
    }

    handleReady() {
        this.setDuration(this.getDuration());
        this.disableCaptions();
    }

    syncPlayhead() {
        this.setPlayhead(this.getCurrentTime());
    }

    seek(time) {
        this.api.currentTime(this.toSS(time));
    }

    pause() {
        this.api.pause();
    }

    isPlaying() {
        return !this.api.paused();
    }

    getDuration() {
        return this.toMS(this.api.duration());
    }

    getCurrentTime() {
        return this.toMS(this.api.currentTime());
    }

    getVideoTitle() {
        return new Promise(resolve => resolve(''));
    }
}



// WEBPACK FOOTER //
// ./scripts/adapters/brightcove.js